import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { useI18n } from "i18n";
import { BROWSER_NAMES, OS_NAMES } from "modules/systemCheck/constants";
import { PermissionState } from "modules/deviceInfo";
import { getPromptAssurance } from "modules/audioVideo/components/DevicePromptPopup/utils";
import { useDevices } from "modules/audioVideo/hooks";
import { useDeviceDetection } from "modules/systemCheck/hooks/useDeviceDetection";
import { getMicCamTroubleshootLink } from "modules/systemCheck/utils/getMicCamTroubleshootLink";
import { DetectionTestBase } from "../DetectionTestBase";
import {
  getDetectionErrorName,
  getDetectionErrorMessage,
  getDetectionErrorStepOne,
  getDetectionError,
} from "./utils";
import {
  Default,
  Chrome,
  Edge,
  Safari,
  AndroidChrome,
  IOSSafari,
} from "./browserPermissionErrors";
import { DevicesInUse } from "./DeviceInUse";

interface Props {
  audioPermissionState: PermissionState;
  videoPermissionState: PermissionState;
  osName: OS_NAMES;
  browserName: BROWSER_NAMES;
}

const PermissionErrorByBrowser = ({
  browserName,
  osName,
  ...rest
}: {
  hasBlockedAudioPermissions: boolean;
  hasBlockedVideoPermissions: boolean;
  browserName: BROWSER_NAMES;
  osName: OS_NAMES;
}) => {
  if (osName === OS_NAMES.ANDROID && browserName === BROWSER_NAMES.CHROME) {
    return <AndroidChrome {...rest} osName={osName} />;
  }

  if (osName === OS_NAMES.IOS && browserName === BROWSER_NAMES.MOBILE_SAFARI) {
    return <IOSSafari {...rest} osName={osName} />;
  }

  if (osName === OS_NAMES.IOS || osName === OS_NAMES.ANDROID) {
    return <Default {...rest} osName={osName} />;
  }

  switch (browserName) {
    case BROWSER_NAMES.CHROME:
      return <Chrome {...rest} osName={osName} />;
    case BROWSER_NAMES.EDGE:
      return <Edge {...rest} osName={osName} />;
    case BROWSER_NAMES.SAFARI:
      return <Safari {...rest} osName={osName} />;
    default:
      return <Default {...rest} osName={osName} />;
  }
};

export const AVDevicesCheck = ({
  audioPermissionState,
  videoPermissionState,
  osName,
  browserName,
}: Props) => {
  const { isCameraErrorType } = useDevices();
  const { t } = useI18n(["micCamCheck", "url", "settings"]);
  const { noSpeaker, noMic, noCam } = useDeviceDetection();

  const hasBlockedAudioPermissions =
    audioPermissionState === PermissionState.BLOCKED ||
    audioPermissionState === PermissionState.PENDING;
  const hasBlockedVideoPermissions =
    videoPermissionState === PermissionState.BLOCKED ||
    audioPermissionState === PermissionState.PENDING;
  const bothDevicesIdle =
    audioPermissionState === PermissionState.IDLE &&
    videoPermissionState === PermissionState.IDLE;

  const hasPermissionIssues =
    hasBlockedAudioPermissions || hasBlockedVideoPermissions;
  const noMicAfterPermissions = !hasBlockedAudioPermissions && noMic;
  const noSpeakerAfterPermissions = !hasBlockedAudioPermissions && noSpeaker;
  const noCamAfterPermissions = !hasBlockedVideoPermissions && noCam;
  const showAnyDeviceMissingError =
    noMicAfterPermissions || noCamAfterPermissions || noSpeakerAfterPermissions;

  return bothDevicesIdle ? (
    <DetectionTestBase
      testName={t("micCamCheck:both.needs.permission")}
      testResult={
        <span>
          <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
            {t("micCamCheck:device.permissions.pending.directions")}
          </Typography>
          <br />
          <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
            {t(getPromptAssurance(true, true))}
          </Typography>
        </span>
      }
      troubleShootLink={t(getMicCamTroubleshootLink(osName))}
      showTroubleshootLink
      hasDivider
    />
  ) : (
    <>
      {hasPermissionIssues && (
        <PermissionErrorByBrowser
          osName={osName}
          browserName={browserName}
          hasBlockedAudioPermissions={hasBlockedAudioPermissions}
          hasBlockedVideoPermissions={hasBlockedVideoPermissions}
        />
      )}
      {showAnyDeviceMissingError && (
        <DetectionTestBase
          testName={t(
            getDetectionErrorName({
              noCam: noCamAfterPermissions,
              noMic: noMicAfterPermissions,
              noSpeaker: noSpeakerAfterPermissions,
            }),
          )}
          testResult={
            <span>
              <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
                {t(
                  getDetectionErrorMessage({
                    noCam: noCamAfterPermissions,
                    noMic: noMicAfterPermissions,
                    noSpeaker: noSpeakerAfterPermissions,
                  }),
                )}
              </Typography>
              <br />

              <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
                {t(
                  getDetectionErrorStepOne(
                    noCamAfterPermissions,
                    noMicAfterPermissions,
                  ),
                )}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
                {t("micCamCheck:device.not.found.step.2")}
              </Typography>
              <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
                {t("micCamCheck:device.not.found.step.3")}
              </Typography>
              {osName === OS_NAMES.MAC && (
                <Typography variant="body1" style={{ whiteSpace: "pre-line" }}>
                  {t(
                    `settings:device.not-found.${getDetectionError({
                      audio: noMicAfterPermissions,
                      video: noCamAfterPermissions,
                      speaker: noSpeakerAfterPermissions,
                    })}.step.4`,
                  )}
                </Typography>
              )}
            </span>
          }
          troubleShootLink={t(getMicCamTroubleshootLink(osName))}
          showTroubleshootLink
          hasDivider
        />
      )}

      {isCameraErrorType && (
        <DevicesInUse osName={osName} isCameraErrorType={isCameraErrorType} />
      )}
    </>
  );
};
